// Task Navigation Slice - Browser-style tabs navigation for related tasks
import { nanoid } from 'nanoid';

// ===== TASK TAB INTERFACE =====

export interface TaskTab {
  id: string; // Unique tab ID (nanoid)
  taskId: string; // Task or Note ID being displayed
  shortId?: string; // URL-friendly ID for cross-panel navigation
  title: string; // Cached title (performance)
  openedAt: Date; // Creation timestamp
  type: 'task' | 'note'; // Tab content type
}

// ===== SLICE INTERFACE =====

export interface TaskNavigationSlice {
  taskNavigation: {
    tabs: TaskTab[]; // Array of open tabs
    activeTabId: string | null; // Currently active tab
    openTab: (
      taskId: string,
      title: string,
      type?: 'task' | 'note',
      shortId?: string
    ) => string;
    closeTab: (tabId: string) => void; // Remove tab, switch to previous
    switchTab: (tabId: string) => void; // Set active tab
    closeAllTabs: () => void; // Clear all tabs
    getTabByTaskId: (taskId: string) => TaskTab | null; // Find tab by task ID
    getTabByIdentifier: (identifier: string) => TaskTab | null;
    updateTabTitle: (tabId: string, newTitle: string) => void; // Update cached title
  };
}

// ===== SLICE CREATOR =====

export const createTaskNavigationSlice = (
  set: (fn: (state: TaskNavigationSlice) => void) => void,
  get: () => TaskNavigationSlice,
  _store: unknown
) => ({
  taskNavigation: {
    tabs: [],
    activeTabId: null,

    /**
     * Open new tab or switch to existing tab
     * Prevents duplicate tabs - if task is already open, switches to that tab
     * @returns Tab ID (new or existing)
     */
    openTab: (
      taskId: string,
      title: string,
      type?: 'task' | 'note',
      shortId?: string
    ): string => {
      const { getTabByTaskId, switchTab } = get().taskNavigation;

      // Check if tab already exists for this task/note
      const existingTab = getTabByTaskId(taskId);
      if (existingTab) {
        // Switch to existing tab instead of creating duplicate
        switchTab(existingTab.id);
        return existingTab.id;
      }

      // Create new tab
      const newTab: TaskTab = {
        id: nanoid(),
        taskId,
        shortId,
        title,
        openedAt: new Date(),
        type: type ?? 'task',
      };

      set((state) => {
        state.taskNavigation.tabs.push(newTab);
        state.taskNavigation.activeTabId = newTab.id;
      });

      return newTab.id;
    },

    /**
     * Close tab and switch to previous or next tab
     * Prefers previous tab (index - 1), falls back to next tab (index + 1) if no previous
     */
    closeTab: (tabId: string): void => {
      set((state) => {
        // Find the tab to close
        const tabIndex = state.taskNavigation.tabs.findIndex(
          (tab) => tab.id === tabId
        );

        if (tabIndex === -1) {
          return; // Tab not found
        }

        const wasActive = state.taskNavigation.activeTabId === tabId;
        let newActiveTabId: string | null = state.taskNavigation.activeTabId;

        // If closing the active tab, determine which tab to switch to
        if (wasActive && state.taskNavigation.tabs.length > 1) {
          // Prefer previous tab (index - 1), fallback to next tab (index + 1)
          if (tabIndex > 0) {
            // Has previous tab, select it
            const previousTab = state.taskNavigation.tabs[tabIndex - 1];
            newActiveTabId = previousTab ? previousTab.id : null;
          } else {
            // No previous tab (closing first tab), select next tab
            const nextTab = state.taskNavigation.tabs[tabIndex + 1];
            newActiveTabId = nextTab ? nextTab.id : null;
          }
        } else if (wasActive) {
          // Last tab, no active tab after close
          newActiveTabId = null;
        }

        // Remove the tab (using filter to avoid index confusion)
        state.taskNavigation.tabs = state.taskNavigation.tabs.filter(
          (tab) => tab.id !== tabId
        );

        // Update active tab
        state.taskNavigation.activeTabId = newActiveTabId;
      });
    },

    /**
     * Switch to specified tab
     * Sets activeTabId if tab exists
     */
    switchTab: (tabId: string): void => {
      set((state) => {
        if (state.taskNavigation.tabs.some((tab) => tab.id === tabId)) {
          state.taskNavigation.activeTabId = tabId;
        }
      });
    },

    /**
     * Close all tabs
     * Clears tabs array and resets activeTabId
     */
    closeAllTabs: (): void => {
      set((state) => {
        state.taskNavigation.tabs = [];
        state.taskNavigation.activeTabId = null;
      });
    },

    /**
     * Find tab by task ID
     * @returns TaskTab if found, null otherwise
     */
    getTabByTaskId: (taskId: string): TaskTab | null => {
      const { tabs } = get().taskNavigation;
      return tabs.find((tab) => tab.taskId === taskId) || null;
    },

    /**
     * Find tab by either task ID or shortId.
     * List entry-points operate on shortId, while internal flows use the GUID
     * taskId — this lets a single lookup serve both.
     */
    getTabByIdentifier: (identifier: string): TaskTab | null => {
      const { tabs } = get().taskNavigation;
      return (
        tabs.find(
          (tab) => tab.taskId === identifier || tab.shortId === identifier
        ) || null
      );
    },

    /**
     * Update tab title
     * Used to sync tab title when task title changes
     */
    updateTabTitle: (tabId: string, newTitle: string): void => {
      set((state) => {
        const targetTab = state.taskNavigation.tabs.find(
          (tab) => tab.id === tabId
        );
        if (targetTab) {
          targetTab.title = newTitle;
        }
      });
    },
  },
});
